import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AddOutlined,
  ContentCopyOutlined,
  DeleteOutlineOutlined,
  EditOutlined,
  LinkOutlined,
} from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import SettingsCard from "../Settings/SettingsCard";
import ConfirmationModal from "../ConfirmationModal";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles()((theme) => ({
  tableWrapper: {
    overflowX: "auto",
  },
  code: {
    display: "inline-block",
    padding: theme.spacing(0.25, 1),
    borderRadius: 6,
    fontFamily: "monospace",
    fontSize: "0.8rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.divider}`,
  },
  campaign: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  message: {
    display: "block",
    maxWidth: 320,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  empty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(4, 2),
  },
  emptyIcon: {
    fontSize: 36,
    color: theme.palette.text.disabled,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    paddingTop: theme.spacing(1),
  },
  preview: {
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: `1px dashed ${theme.palette.divider}`,
  },
  previewLabel: {
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  previewMessage: {
    fontSize: "0.9rem",
  },
  previewHint: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
}));

const emptyForm = () => ({ name: "", phone: "", message: i18n.t("settings.googleAds.links.defaultMessage"), queueId: "", googleCampaignId: "" });

const messageWithCode = (message, code) =>
  [String(message || "").trim(), code ? `[${code}]` : ""].filter(Boolean).join(" ");

const whatsappLinkOf = (phone, message, code) =>
  `https://wa.me/${String(phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(
    messageWithCode(message, code)
  )}`;

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(i18n.t("settings.googleAds.links.linkCopied"));
  } catch (err) {
    toast.error(i18n.t("settings.googleAds.links.copyError"));
  }
};

const GoogleAdsLinks = () => {
  const { classes } = useStyles();

  const [links, setLinks] = useState([]);
  const [queues, setQueues] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [defaultPhone, setDefaultPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleting, setDeleting] = useState(null);

  const loadCampaigns = async () => {
    try {
      const { data } = await api.get("/google-ads/campaigns");
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (err) {
      setCampaigns([]);
    }
  };

  const loadLinks = async () => {
    const { data } = await api.get("/google-ads-links");
    setLinks(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [, { data: queueList }, , { data: connections }] = await Promise.all([
          loadLinks(),
          api.get("/queue"),
          loadCampaigns(),
          api.get("/whatsapp/"),
        ]);
        setQueues(Array.isArray(queueList) ? queueList : []);
        const withNumber = (Array.isArray(connections) ? connections : []).find(
          (w) => ["whatsapp", "whatsapp_cloud"].includes(w.channel) && w.number
        );
        setDefaultPhone(withNumber?.number || "");
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openCreate = () => {
    setForm({ ...emptyForm(), phone: defaultPhone });
    setEditing({});
    loadCampaigns();
  };

  const openEdit = (link) => {
    setForm({
      name: link.name || "",
      phone: link.phone || "",
      message: link.message || "",
      queueId: link.queueId ? String(link.queueId) : "",
      googleCampaignId: link.googleCampaignId || "",
    });
    setEditing(link);
    loadCampaigns();
  };

  const campaignNameOf = (id) =>
    campaigns.find((c) => c.adId === id)?.campaignName || id;

  const isActiveCampaign = (campaign) => !campaign.status || campaign.status === "ENABLED";

  const selectedCampaign = campaigns.find((c) => c.adId === form.googleCampaignId);

  const campaignOptions = campaigns.filter(
    (c) => isActiveCampaign(c) || c.adId === form.googleCampaignId
  );

  const closeForm = () => {
    if (saving) return;
    setEditing(null);
  };

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);

    try {
      const payload = { ...form, queueId: form.queueId || null };
      if (editing?.id) {
        await api.put(`/google-ads-links/${editing.id}`, payload);
        toast.success(i18n.t("settings.googleAds.links.updated"));
      } else {
        await api.post("/google-ads-links", payload);
        toast.success(i18n.t("settings.googleAds.links.created"));
      }
      await loadLinks();
      setEditing(null);
    } catch (err) {
      toastError(err);
    }

    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/google-ads-links/${deleting.id}`);
      toast.success(i18n.t("settings.googleAds.links.deleted"));
      await loadLinks();
    } catch (err) {
      toastError(err);
    }
    setDeleting(null);
  };

  return (
    <SettingsCard
      icon={LinkOutlined}
      title={i18n.t("settings.googleAds.links.title")}
      subtitle={i18n.t("settings.googleAds.links.subtitle")}
      action={
        <Button size="small" variant="contained" startIcon={<AddOutlined />} onClick={openCreate}>
          {i18n.t("settings.googleAds.links.newLink")}
        </Button>
      }
    >
      <Box className={classes.tableWrapper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "22%" }}>{i18n.t("settings.googleAds.links.name")}</TableCell>
              <TableCell sx={{ width: 110 }}>{i18n.t("settings.googleAds.links.code")}</TableCell>
              <TableCell sx={{ width: 150 }}>{i18n.t("settings.googleAds.links.phone")}</TableCell>
              <TableCell>{i18n.t("settings.googleAds.links.message")}</TableCell>
              <TableCell sx={{ width: 170 }}>{i18n.t("settings.googleAds.links.queue")}</TableCell>
              <TableCell align="right" sx={{ width: 130 }}>{i18n.t("settings.googleAds.links.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && links.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ borderBottom: "none" }}>
                  <Box className={classes.empty}>
                    <LinkOutlined className={classes.emptyIcon} />
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("settings.googleAds.links.empty")}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {links.map((link) => (
              <TableRow key={link.id} hover>
                <TableCell>
                  <div>{link.name}</div>
                  {link.googleCampaignId && (
                    <div className={classes.campaign}>
                      {campaignNameOf(link.googleCampaignId)}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <span className={classes.code}>{link.code}</span>
                </TableCell>
                <TableCell>{link.phone}</TableCell>
                <TableCell>
                  <Tooltip title={messageWithCode(link.message, link.code)}>
                    <span className={classes.message}>{link.message || "—"}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {link.queue ? (
                    <Chip
                      label={link.queue.name}
                      size="small"
                      sx={{ backgroundColor: link.queue.color, color: "#fff" }}
                    />
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <Tooltip title={i18n.t("settings.googleAds.links.copyLink")}>
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(whatsappLinkOf(link.phone, link.message, link.code))}
                    >
                      <ContentCopyOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={i18n.t("settings.googleAds.links.edit")}>
                    <IconButton size="small" onClick={() => openEdit(link)}>
                      <EditOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={i18n.t("settings.googleAds.links.delete")}>
                    <IconButton size="small" onClick={() => setDeleting(link)}>
                      <DeleteOutlineOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Dialog open={!!editing} onClose={closeForm} fullWidth maxWidth="sm">
        <DialogTitle>{editing?.id ? i18n.t("settings.googleAds.links.editTitle") : i18n.t("settings.googleAds.links.newTitle")}</DialogTitle>
        <DialogContent dividers>
          <Box className={classes.form}>
            <TextField
              label={i18n.t("settings.googleAds.links.nameLabel")}
              size="small"
              fullWidth
              autoFocus
              placeholder={i18n.t("settings.googleAds.links.namePlaceholder")}
              helperText={i18n.t("settings.googleAds.links.nameHelp")}
              value={form.name}
              onChange={handleChange("name")}
            />
            <TextField
              select
              label={i18n.t("settings.googleAds.links.campaignLabel")}
              size="small"
              fullWidth
              value={form.googleCampaignId}
              onChange={handleChange("googleCampaignId")}
              helperText={
                campaigns.length
                  ? i18n.t("settings.googleAds.links.campaignHelp")
                  : i18n.t("settings.googleAds.links.campaignEmpty")
              }
              slotProps={{ inputLabel: { shrink: true }, select: { displayEmpty: true } }}
            >
              <MenuItem value="">{i18n.t("settings.googleAds.links.noCampaign")}</MenuItem>
              {form.googleCampaignId && !selectedCampaign && (
                <MenuItem value={form.googleCampaignId}>{form.googleCampaignId}</MenuItem>
              )}
              {campaignOptions.map((c) => (
                <MenuItem key={c.adId} value={c.adId}>
                  {c.campaignName || c.adId}
                  {isActiveCampaign(c) ? "" : ` ${i18n.t("settings.googleAds.links.campaignPaused")}`}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={i18n.t("settings.googleAds.links.phoneLabel")}
              size="small"
              fullWidth
              placeholder="593963111122"
              helperText={i18n.t("settings.googleAds.links.phoneHelp")}
              value={form.phone}
              onChange={handleChange("phone")}
            />
            <TextField
              label={i18n.t("settings.googleAds.links.messageLabel")}
              size="small"
              fullWidth
              multiline
              minRows={2}
              helperText={i18n.t("settings.googleAds.links.messageHelp")}
              value={form.message}
              onChange={handleChange("message")}
            />
            <TextField
              select
              label={i18n.t("settings.googleAds.links.queue")}
              size="small"
              fullWidth
              value={form.queueId}
              onChange={handleChange("queueId")}
              helperText={i18n.t("settings.googleAds.links.queueHelp")}
              slotProps={{ inputLabel: { shrink: true }, select: { displayEmpty: true } }}
            >
              <MenuItem value="">{i18n.t("settings.googleAds.links.noQueue")}</MenuItem>
              {queues.map((q) => (
                <MenuItem key={q.id} value={String(q.id)}>
                  {q.name}
                </MenuItem>
              ))}
            </TextField>
            <Box className={classes.preview}>
              <Typography className={classes.previewLabel}>{i18n.t("settings.googleAds.links.previewLabel")}</Typography>
              <Typography className={classes.previewMessage}>
                {messageWithCode(form.message, editing?.code || "G-····")}
              </Typography>
              {!editing?.code && (
                <Typography className={classes.previewHint}>
                  {i18n.t("settings.googleAds.links.previewHint")}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm} disabled={saving}>
            {i18n.t("settings.googleAds.links.cancel")}
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {i18n.t("settings.googleAds.links.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title={i18n.t("settings.googleAds.links.deleteTitle")}
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
      >
        {deleting
          ? i18n.t("settings.googleAds.links.deleteConfirm", { name: deleting.name, code: deleting.code, interpolation: { escapeValue: false } })
          : ""}
      </ConfirmationModal>
    </SettingsCard>
  );
};

export default GoogleAdsLinks;
